import React from 'react';
import PropTypes from 'prop-types';

/**
 * OptionsList — renders the dropdown’s scrollable list of checkbox options.
 *
 * Keyboard & accessibility:
 *  • `role="listbox"` on the <ul>
 *  • Each <li> has `role="option"` and `aria-selected`
 *  • `tabIndex` moves focus to the highlighted item
 *
 * Props:
 * @param {{ id: string, name: string }[]} options
 *    — All available options to render.
 * @param {string[]} selectedIds
 *    — IDs of the options currently selected.
 * @param {number} highlightedIndex
 *    — Index of the option currently highlighted (for keyboard nav).
 * @param {(id: string) => void} onToggle
 *    — Called when an option is clicked/activated to toggle selection.
 * @param {(index: number) => void} onHover
 *    — Called on mouseEnter to update the highlightedIndex.
 * @param {Object} [listStyle]
 *    — Optional inline style for the list (e.g. when rendered in a portal with position: fixed).
 */
const OptionsList = ({ options, selectedIds, highlightedIndex, onToggle, onHover, listStyle }) => (
  <ul
    id="multiselect-list"
    role="listbox"
    tabIndex={-1}
    className={`z-[100] mt-1 overflow-auto bg-white border rounded-md shadow-lg ${listStyle ? '' : 'absolute w-full max-h-64'}`}
    style={listStyle}
  >
    {options.map((opt, i) => {
      const checked = selectedIds.includes(opt.id);
      return (
        <li
          key={opt.id}
          role="option"
          aria-selected={checked}
          tabIndex={highlightedIndex === i ? 0 : -1}
          className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 cursor-pointer"
          onClick={() => onToggle(opt.id)}
          onMouseEnter={() => onHover(i)}
        >
          <div className="relative shrink-0">
            <input
              type="checkbox"
              checked={checked}
              readOnly
              className="size-[18px] appearance-none bg-white border-2 border-[#9e9e9e] rounded-[3px] cursor-pointer checked:bg-[#61b8e7] checked:border-[#61b8e7] pointer-events-none"
              tabIndex={-1}
            />
            {checked && (
              <svg
                className="absolute left-[3px] top-[3px] w-[12px] h-[12px] pointer-events-none"
                fill="none"
                viewBox="0 0 12 10"
              >
                <path
                  d="M1.5 5L4.5 8L10.5 1.5"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <span className="text-black">{opt.name}</span>
        </li>
      );
    })}
  </ul>
);

OptionsList.propTypes = {
  options: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
  highlightedIndex: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired,
  listStyle: PropTypes.object,
};

export default OptionsList;
